<template>
    <Transition>
        <div
            v-if="showPopover"
            ref="popover"
            class="fixed w-[200px] bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg z-[85] overflow-hidden p-2"
            :style="popoverStyle"
        >
            <div class="grid grid-cols-5 gap-1">
                <button
                    v-for="color in predefinedColors"
                    :key="color.value"
                    @click="selectColor(color.value)"
                    class="w-8 h-8 rounded border transition-all hover:scale-110"
                    :class="{ 
                        'border-gray-800 dark:border-white shadow-[0_0_0_2px_rgba(99,102,241,1)]': selectedColor === color.value,
                        'border-gray-200 dark:border-slate-600': selectedColor !== color.value
                    }"
                    :style="{ backgroundColor: color.value }"
                    :title="color.name"
                ></button>
            </div>
            <button
                v-if="showClearButton"
                @click="clearColor"
                class="w-full mt-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors border-t border-gray-200 dark:border-slate-700 rounded"
            >
                Clear Style
            </button>
        </div>
    </Transition>
</template>

<script setup>
import { ref, computed, defineEmits, defineProps, defineExpose, watch, onMounted, onUnmounted, nextTick } from 'vue'

const emits = defineEmits(['change', 'applyColor', 'clearColor'])
const props = defineProps({
    showPicker: {
        type: Boolean,
        default: false
    },
    triggerElement: {
        type: Object,
        default: null
    },
    colorType: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'pastel', 'default-opacity'].includes(value)
    },
    position: {
        type: String,
        default: 'center-screen',
        validator: (value) => ['center-screen', 'center-top', 'center-bottom'].includes(value)
    },
    showClearButton: {
        type: Boolean,
        default: false
    },
    defaultPosition: {
        type: Object,
        default: null
    }
})

const showPopover = ref(false)
const popover = ref(null)
const selectedColor = ref(null)
const popoverStyle = ref({})

// Default (non-pastel) predefined colors
const defaultColors = [
    { name: 'Black', value: '#000000' },
    { name: 'Dark Gray', value: '#333333' },
    { name: 'Gray', value: '#666666' },
    { name: 'Light Gray', value: '#999999' },
    { name: 'White', value: '#FFFFFF' },
    { name: 'Red', value: '#DC2626' },
    { name: 'Dark Red', value: '#991B1B' },
    { name: 'Orange', value: '#EA580C' },
    { name: 'Dark Orange', value: '#C2410C' },
    { name: 'Yellow', value: '#CA8A04' },
    { name: 'Dark Yellow', value: '#854D0E' },
    { name: 'Green', value: '#16A34A' },
    { name: 'Dark Green', value: '#15803D' },
    { name: 'Teal', value: '#0D9488' },
    { name: 'Dark Teal', value: '#0F766E' },
    { name: 'Blue', value: '#2563EB' },
    { name: 'Dark Blue', value: '#1E40AF' },
    { name: 'Indigo', value: '#4F46E5' },
    { name: 'Dark Indigo', value: '#4338CA' },
    { name: 'Purple', value: '#9333EA' },
    { name: 'Dark Purple', value: '#7E22CE' },
    { name: 'Pink', value: '#DB2777' },
    { name: 'Dark Pink', value: '#BE185D' },
    { name: 'Brown', value: '#92400E' },
    { name: 'Dark Brown', value: '#78350F' },
    { name: 'Maroon', value: '#991B1B' },
    { name: 'Navy', value: '#1E3A8A' },
    { name: 'Cyan', value: '#0891B2' },
    { name: 'Magenta', value: '#C026D3' }
]

// Pastel colors
const pastelColors = [
    { name: 'White', value: '#FFFFFF' },
    { name: 'Light Pink', value: '#FFB3D9' },
    { name: 'Peach', value: '#FFCCCB' },
    { name: 'Lavender', value: '#E6E6FA' },
    { name: 'Mint', value: '#B2F5EA' },
    { name: 'Sky Blue', value: '#87CEEB' },
    { name: 'Powder Blue', value: '#B0E0E6' },
    { name: 'Lavender Blue', value: '#CCCCFF' },
    { name: 'Lilac', value: '#DDA0DD' },
    { name: 'Rose', value: '#FFE4E1' },
    { name: 'Apricot', value: '#FBCEB1' },
    { name: 'Cream', value: '#FFFDD0' },
    { name: 'Lemon Chiffon', value: '#FFFACD' },
    { name: 'Honeydew', value: '#F0FFF0' },
    { name: 'Mint Cream', value: '#F5FFFA' },
    { name: 'Alice Blue', value: '#F0F8FF' },
    { name: 'Lavender Blush', value: '#FFF0F5' },
    { name: 'Seashell', value: '#FFF5EE' },
    { name: 'Beige', value: '#F5F5DC' },
    { name: 'Ivory', value: '#FFFFF0' },
    { name: 'Light Yellow', value: '#FFFFE0' },
    { name: 'Light Green', value: '#90EE90' },
    { name: 'Light Cyan', value: '#E0FFFF' },
    { name: 'Light Blue', value: '#ADD8E6' },
    { name: 'Light Steel Blue', value: '#B0C4DE' },
    { name: 'Thistle', value: '#D8BFD8' },
    { name: 'Plum', value: '#DDA0DD' },
    { name: 'Misty Rose', value: '#FFE4E1' },
    { name: 'Antique White', value: '#FAEBD7' },
    { name: 'Linen', value: '#FAF0E6' }
]

// Default colors with opacity (rgba format with 0.5 opacity)
const defaultColorsWithOpacity = [
    { name: 'Black', value: 'rgba(0, 0, 0, 0.5)' },
    { name: 'Dark Gray', value: 'rgba(51, 51, 51, 0.5)' },
    { name: 'Gray', value: 'rgba(102, 102, 102, 0.5)' },
    { name: 'Light Gray', value: 'rgba(153, 153, 153, 0.5)' },
    { name: 'White', value: 'rgba(255, 255, 255, 0.5)' },
    { name: 'Red', value: 'rgba(220, 38, 38, 0.5)' },
    { name: 'Dark Red', value: 'rgba(153, 27, 27, 0.5)' },
    { name: 'Orange', value: 'rgba(234, 88, 12, 0.5)' },
    { name: 'Dark Orange', value: 'rgba(194, 65, 12, 0.5)' },
    { name: 'Yellow', value: 'rgba(202, 138, 4, 0.5)' },
    { name: 'Dark Yellow', value: 'rgba(133, 77, 14, 0.5)' },
    { name: 'Green', value: 'rgba(22, 163, 74, 0.5)' },
    { name: 'Dark Green', value: 'rgba(21, 128, 61, 0.5)' },
    { name: 'Teal', value: 'rgba(13, 148, 136, 0.5)' },
    { name: 'Dark Teal', value: 'rgba(15, 118, 110, 0.5)' },
    { name: 'Blue', value: 'rgba(37, 99, 235, 0.5)' },
    { name: 'Dark Blue', value: 'rgba(30, 64, 175, 0.5)' },
    { name: 'Indigo', value: 'rgba(79, 70, 229, 0.5)' },
    { name: 'Dark Indigo', value: 'rgba(67, 56, 202, 0.5)' },
    { name: 'Purple', value: 'rgba(147, 51, 234, 0.5)' },
    { name: 'Dark Purple', value: 'rgba(126, 34, 206, 0.5)' },
    { name: 'Pink', value: 'rgba(219, 39, 119, 0.5)' },
    { name: 'Dark Pink', value: 'rgba(190, 24, 93, 0.5)' },
    { name: 'Brown', value: 'rgba(146, 64, 14, 0.5)' },
    { name: 'Dark Brown', value: 'rgba(120, 53, 15, 0.5)' },
    { name: 'Maroon', value: 'rgba(153, 27, 27, 0.5)' },
    { name: 'Navy', value: 'rgba(30, 58, 138, 0.5)' },
    { name: 'Cyan', value: 'rgba(8, 145, 178, 0.5)' },
    { name: 'Magenta', value: 'rgba(192, 38, 211, 0.5)' }
]

// Computed property to get the appropriate color list based on colorType
const predefinedColors = computed(() => {
    switch (props.colorType) {
        case 'pastel':
            return pastelColors
        case 'default-opacity':
            return defaultColorsWithOpacity
        case 'default':
        default:
            return defaultColors
    }
})

const openColorPicker = async (triggerEl = null) => {
    showPopover.value = true
    selectedColor.value = null
    
    await nextTick()
    
    if (triggerEl) {
        await positionPopover(triggerEl)
    }
}

const positionPopover = async (triggerEl) => {

    if (props.defaultPosition) {        
        popoverStyle.value = {
            position: props.defaultPosition.position || 'fixed',
            left: `${props.defaultPosition.left || 0}px`,
            top: `${props.defaultPosition.top || 0}px`,
            zIndex: 99999
        }
        return
    }
    let left = 0
    let top = 0
    if (!popover.value) return
    
    const popoverRect = popover.value.getBoundingClientRect()

    if (!triggerEl) {
        if (props.position === 'center-screen') {
            left = window.innerWidth / 2 - popoverRect.width / 2
            top = window.innerHeight / 2 - popoverRect.height / 2
        } else if (props.position === 'center-top') {
            left = window.innerWidth / 2 - popoverRect.width / 2
            top = 8
        } else if (props.position === 'center-bottom') {
            left = window.innerWidth / 2 - popoverRect.width / 2
            top = window.innerHeight - popoverRect.height - 8
        }
    } else {
    
        await nextTick()
        
        // Handle Vue ref - get the actual DOM element
        const actualTriggerEl = triggerEl.$el || triggerEl
        if (!actualTriggerEl) return
        
        const rect = actualTriggerEl.getBoundingClientRect()
        
        
        // Position below the trigger element, centered
        left = rect.left + (rect.width / 2) - (popoverRect.width / 2)
        top = rect.bottom + 4
        // Adjust if popover would go off screen horizontally
        if (left < 8) {
            left = 8
        } else if (left + popoverRect.width > window.innerWidth - 8) {
            left = window.innerWidth - popoverRect.width - 8
        }
        
        // Adjust if popover would go off screen vertically
        if (top + popoverRect.height > window.innerHeight - 8) {
            top = rect.top - popoverRect.height - 4
            // If still doesn't fit, position at top of screen
            if (top < 8) {
                top = 8
            }
        }
    }
    popoverStyle.value = {
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        zIndex: 99999
    }
}

const selectColor = (color) => {
    selectedColor.value = color
    emits('change', { target: { value: color } })
    emits('applyColor', color)
    showPopover.value = false
}

const clearColor = () => {
    emits('clearColor')
    showPopover.value = false
}

const handleClickOutside = (event) => {
    if (!showPopover.value || !popover.value) return
    
    // Check if click is inside the popover
    if (popover.value.contains(event.target)) {
        return
    }
    
    // Check if click is on the trigger element
    const triggerEl = props.triggerElement?.$el || props.triggerElement
    if (triggerEl && (triggerEl === event.target || triggerEl.contains(event.target))) {
        return
    }
    
    showPopover.value = false
}

watch(() => props.showPicker, (newValue) => {
    if (newValue) {
        openColorPicker(props.triggerElement)
    } else {
        showPopover.value = false
    }
})

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})

defineExpose({
    open: openColorPicker,
    close: () => {
        showPopover.value = false
    },
    toggle: () => {
        showPopover.value = !showPopover.value;
        if (showPopover.value) {
            openColorPicker(props.triggerElement)
        } else {
            showPopover.value = false
        }
    }
})
</script>